import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styles from './AdminShared.module.scss';
import {
    AdminCollectionAutomationConfigurationUpdateDto,
    CollectionCreateDto,
    CollectionDto,
    HighlightRolloverIntervalUnit,
    TagDto
} from '../../../types/admin.types';
import {
    createAdminCollection,
    createAdminTag,
    deleteAdminCollection,
    fetchAdminCollections,
    fetchAdminTags,
    fetchCollectionAutomationConfiguration,
    updateAdminCollection,
    updateCollectionAutomationConfiguration
} from '../../../services/admin.service';
import { useUserState } from '../../../providers/user-state.provider.ts';
import ImagePickerField from './components/ImagePickerField';
import AdminDateTimeInput from './components/AdminDateTimeInput';
import { toDateTimeLocalInput, toUtcIsoString } from '../../../utils/date-time';
import { isSuperAdmin } from '../../../utils/roles';

const emptyForm: CollectionCreateDto = {
    title: '',
    description: '',
    imageUrl: '',
    tags: [],
    startDate: '',
    endDate: '',
    isActive: true,
    sortOrder: 0
};

const normalizeName = (value?: string | null) => (value ?? '').trim().toLowerCase();

const AdminCollections = () => {
    const { getUserInfo } = useUserState();
    const [items, setItems] = useState<CollectionDto[]>([]);
    const [tags, setTags] = useState<TagDto[]>([]);
    const [form, setForm] = useState<CollectionCreateDto>(emptyForm);
    const [configForm, setConfigForm] = useState<AdminCollectionAutomationConfigurationUpdateDto | null>(null);
    const [tagInput, setTagInput] = useState('');
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSavingConfig, setIsSavingConfig] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [configErrorMessage, setConfigErrorMessage] = useState<string | null>(null);
    const [configSuccessMessage, setConfigSuccessMessage] = useState<string | null>(null);
    const user = getUserInfo();
    const token = user?.token;
    const canManageAutomation = isSuperAdmin(user?.roles);

    const mapCollection = (item: any): CollectionDto => ({
        id: item.id ?? item.Id ?? '',
        title: item.title ?? item.Title ?? '',
        description: item.description ?? item.Description ?? null,
        imageUrl: item.imageUrl ?? item.ImageUrl ?? '',
        tags: item.tags ?? item.Tags ?? [],
        startDate: item.startDate ?? item.StartDate ?? null,
        endDate: item.endDate ?? item.EndDate ?? null,
        createdDate: item.createdDate ?? item.CreatedDate ?? '',
        updatedDate: item.updatedDate ?? item.UpdatedDate ?? '',
        isActive: item.isActive ?? item.IsActive ?? false,
        sortOrder: item.sortOrder ?? item.SortOrder ?? 0
    });

    const loadItems = useCallback(async () => {
        setIsLoading(true);
        setErrorMessage(null);
        try {
            const [collectionsResponse, tagsResponse] = await Promise.all([
                fetchAdminCollections(token),
                fetchAdminTags(token)
            ]);
            setItems((collectionsResponse ?? []).map(mapCollection));
            setTags((tagsResponse ?? []).map((item: any) => ({
                id: item.id ?? item.Id ?? '',
                name: item.name ?? item.Name ?? '',
                usageCount: item.usageCount ?? item.UsageCount ?? 0,
                lastUsedDate: item.lastUsedDate ?? item.LastUsedDate ?? null
            })));
        } catch (error) {
            setErrorMessage('Unable to load collections.');
        } finally {
            setIsLoading(false);
        }
    }, [token]);

    const loadConfig = useCallback(async () => {
        if (!canManageAutomation) {
            setConfigForm(null);
            return;
        }

        setConfigErrorMessage(null);
        try {
            const response = await fetchCollectionAutomationConfiguration(token);
            setConfigForm({
                Id: response.Id,
                EnableDynamicCollections: response.EnableDynamicCollections ?? false,
                CollectionsRolloverIntervalValue: response.CollectionsRolloverIntervalValue ?? 6,
                CollectionsRolloverIntervalUnit: response.CollectionsRolloverIntervalUnit ?? 'Hours',
                CollectionsDisplayCount: response.CollectionsDisplayCount ?? 3,
                CollectionsTemplateSet: response.CollectionsTemplateSet ?? 'default'
            });
        } catch (error) {
            setConfigForm(null);
            setConfigErrorMessage('Unable to load collections automation settings.');
        }
    }, [canManageAutomation, token]);

    useEffect(() => {
        loadItems();
        loadConfig();
    }, [loadItems, loadConfig]);

    const resetForm = () => {
        setForm(emptyForm);
        setTagInput('');
        setSelectedId(null);
    };

    const availableTags = useMemo(
        () => tags
            .map((tag) => tag.name)
            .filter((tagName) => !form.tags.some((value) => normalizeName(value) === normalizeName(tagName))),
        [form.tags, tags]
    );

    const handleEdit = (item: CollectionDto) => {
        setSelectedId(item.id);
        setForm({
            title: item.title,
            description: item.description ?? '',
            imageUrl: item.imageUrl,
            tags: item.tags ?? [],
            startDate: toDateTimeLocalInput(item.startDate),
            endDate: toDateTimeLocalInput(item.endDate),
            isActive: item.isActive,
            sortOrder: item.sortOrder
        });
        setTagInput('');
    };

    const addExistingTagByName = (value: string) => {
        const trimmed = value.trim();
        if (!trimmed) {
            return;
        }

        const normalized = normalizeName(trimmed);
        const existing = tags.find((tag) => normalizeName(tag?.name) === normalized);
        if (!existing) {
            return;
        }

        setForm((current) => {
            if (current.tags.some((tag) => normalizeName(tag) === normalized)) {
                return current;
            }

            return {
                ...current,
                tags: [...current.tags, existing.name]
            };
        });
    };

    const removeTag = (value: string) => {
        setForm((current) => ({
            ...current,
            tags: current.tags.filter((tag) => tag !== value)
        }));
    };

    const createTag = async () => {
        const trimmed = tagInput.trim();
        if (!trimmed) {
            return;
        }

        const normalized = normalizeName(trimmed);
        if (form.tags.some((tag) => normalizeName(tag) === normalized)) {
            setTagInput('');
            return;
        }

        const existing = tags.find((tag) => normalizeName(tag?.name) === normalized);
        if (existing) {
            setForm((current) => ({
                ...current,
                tags: current.tags.some((tag) => normalizeName(tag) === normalized)
                    ? current.tags
                    : [...current.tags, existing.name]
            }));
            setTagInput('');
            return;
        }

        try {
            const created = await createAdminTag({ name: trimmed }, token);
            const createdName = created?.name ?? trimmed;
            const normalizedCreated: TagDto = {
                id: created?.id ?? '',
                name: createdName,
                usageCount: created?.usageCount ?? 0,
                lastUsedDate: created?.lastUsedDate ?? null
            };

            setTags((current) => [...current, normalizedCreated]);
            setForm((current) => ({
                ...current,
                tags: [...current.tags, createdName]
            }));
            setTagInput('');
        } catch (error: any) {
            setErrorMessage(error?.message || 'Unable to create tag.');
        }
    };

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        setErrorMessage(null);
        const payload = {
            ...form,
            startDate: toUtcIsoString(form.startDate),
            endDate: toUtcIsoString(form.endDate)
        };

        try {
            if (selectedId) {
                await updateAdminCollection(selectedId, payload, token);
            } else {
                await createAdminCollection(payload, token);
            }
            await loadItems();
            resetForm();
        } catch (error: any) {
            setErrorMessage(error?.message || 'Unable to save collection.');
        }
    };

    const handleDelete = async (id: string) => {
        setErrorMessage(null);
        try {
            await deleteAdminCollection(id, token);
            if (selectedId === id) {
                resetForm();
            }
            await loadItems();
        } catch (error) {
            setErrorMessage('Unable to delete collection.');
        }
    };

    const handleConfigSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        if (!configForm || !canManageAutomation) {
            return;
        }

        const normalizedIntervalValue = Math.max(1, Math.min(99, Number(configForm.CollectionsRolloverIntervalValue ?? 6)));
        const normalizedDisplayCount = Math.max(1, Math.min(99, Number(configForm.CollectionsDisplayCount ?? 3)));

        const payload: AdminCollectionAutomationConfigurationUpdateDto = {
            ...configForm,
            EnableDynamicCollections: Boolean(configForm.EnableDynamicCollections),
            CollectionsRolloverIntervalValue: normalizedIntervalValue,
            CollectionsRolloverIntervalUnit: configForm.CollectionsRolloverIntervalUnit ?? 'Hours',
            CollectionsDisplayCount: normalizedDisplayCount,
            CollectionsTemplateSet: configForm.CollectionsTemplateSet ?? 'default'
        };

        setConfigErrorMessage(null);
        setConfigSuccessMessage(null);
        setIsSavingConfig(true);

        try {
            await updateCollectionAutomationConfiguration(payload, token);
            setConfigSuccessMessage('Collections automation settings saved.');
            await loadConfig();
        } catch (error) {
            setConfigErrorMessage('Unable to save collections automation settings.');
        } finally {
            setIsSavingConfig(false);
        }
    };

    const handleCollectionIntervalUnitChange = (value: string) => {
        if (!configForm) {
            return;
        }

        const units: HighlightRolloverIntervalUnit[] = ['Minutes', 'Hours', 'Days', 'Weeks'];
        const nextUnit = units.includes(value as HighlightRolloverIntervalUnit)
            ? (value as HighlightRolloverIntervalUnit)
            : 'Hours';

        setConfigForm({ ...configForm, CollectionsRolloverIntervalUnit: nextUnit });
    };

    return (
        <div className={styles.page}>
            <header className={styles.header}>
                <div>
                    <h1>Collections</h1>
                    <p>Manage curated collection rails.</p>
                </div>
            </header>

            {errorMessage ? <div className={styles.muted}>{errorMessage}</div> : null}

            <div className={styles.content}>
                {canManageAutomation ? (
                    <section className={styles.panel}>
                        <h2>Collections Automation</h2>
                        {configErrorMessage ? <div className={styles.muted}>{configErrorMessage}</div> : null}
                        {configSuccessMessage ? <div className={styles.muted}>{configSuccessMessage}</div> : null}
                        {configForm ? (
                            <form onSubmit={handleConfigSubmit}>
                                <div className={styles.field}>
                                    <label>
                                        <input
                                            type="checkbox"
                                            checked={Boolean(configForm.EnableDynamicCollections)}
                                            onChange={(event) => setConfigForm({ ...configForm, EnableDynamicCollections: event.target.checked })}
                                        />
                                        {' '}Enable auto-generated collections
                                    </label>
                                </div>
                                <div className={styles.field}>
                                    <label htmlFor="collection-rollover-interval">Rollover Interval</label>
                                    <input
                                        id="collection-rollover-interval"
                                        type="number"
                                        min={1}
                                        max={99}
                                        value={configForm.CollectionsRolloverIntervalValue ?? 6}
                                        onChange={(event) =>
                                            setConfigForm({
                                                ...configForm,
                                                CollectionsRolloverIntervalValue: event.target.value === '' ? 1 : Number(event.target.value)
                                            })
                                        }
                                    />
                                </div>
                                <div className={styles.field}>
                                    <label htmlFor="collection-rollover-unit">Interval Unit</label>
                                    <select
                                        id="collection-rollover-unit"
                                        value={configForm.CollectionsRolloverIntervalUnit ?? 'Hours'}
                                        onChange={(event) => handleCollectionIntervalUnitChange(event.target.value)}
                                    >
                                        <option value="Minutes">Minutes</option>
                                        <option value="Hours">Hours</option>
                                        <option value="Days">Days</option>
                                        <option value="Weeks">Weeks</option>
                                    </select>
                                </div>
                                <div className={styles.field}>
                                    <label htmlFor="collection-template-set">Template Set</label>
                                    <select
                                        id="collection-template-set"
                                        value={configForm.CollectionsTemplateSet ?? 'default'}
                                        onChange={(event) =>
                                            setConfigForm({
                                                ...configForm,
                                                CollectionsTemplateSet: event.target.value
                                            })
                                        }
                                    >
                                        <option value="default">Default</option>
                                        <option value="compact">Compact</option>
                                        <option value="spotlight">Spotlight</option>
                                    </select>
                                </div>
                                <div className={styles.field}>
                                    <label htmlFor="collection-display-count">Display Count</label>
                                    <select
                                        id="collection-display-count"
                                        value={configForm.CollectionsDisplayCount ?? 3}
                                        onChange={(event) =>
                                            setConfigForm({ ...configForm, CollectionsDisplayCount: Number(event.target.value) })
                                        }
                                    >
                                        {Array.from({ length: 99 }, (_, index) => index + 1).map((value) => (
                                            <option key={value} value={value}>
                                                {value}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className={styles.actions}>
                                    <button className={styles.primary} type="submit" disabled={isSavingConfig}>
                                        {isSavingConfig ? 'Saving...' : 'Save Automation Settings'}
                                    </button>
                                </div>
                            </form>
                        ) : configErrorMessage ? null : (
                            <div className={styles.muted}>Loading automation settings...</div>
                        )}
                    </section>
                ) : null}

                <section className={styles.panel}>
                    <h2>Current Collections</h2>
                    {isLoading ? (
                        <div className={styles.muted}>Loading collections...</div>
                    ) : items.length === 0 ? (
                        <div className={styles.empty}>No collections yet.</div>
                    ) : (
                        items.map((item) => (
                            <div key={item.id} className={styles.listItem}>
                                <div>
                                    <strong>{item.title}</strong>
                                    <div className={styles.muted}>Tags: {item.tags.join(', ') || 'None'}</div>
                                </div>
                                <div className={styles.rowActions}>
                                    <button type="button" className={styles.secondary} onClick={() => handleEdit(item)}>Edit</button>
                                    <button type="button" className={styles.danger} onClick={() => handleDelete(item.id)}>Delete</button>
                                </div>
                            </div>
                        ))
                    )}
                </section>

                <section className={styles.panel}>
                    <h2>{selectedId ? 'Edit Collection' : 'New Collection'}</h2>
                    <form onSubmit={handleSubmit}>
                        <div className={styles.field}>
                            <label htmlFor="collection-title">Title</label>
                            <input
                                id="collection-title"
                                value={form.title}
                                onChange={(event) => setForm({ ...form, title: event.target.value })}
                                maxLength={200}
                                required
                            />
                        </div>
                        <div className={styles.field}>
                            <label htmlFor="collection-description">Description</label>
                            <textarea
                                id="collection-description"
                                value={form.description ?? ''}
                                onChange={(event) => setForm({ ...form, description: event.target.value })}
                                maxLength={2000}
                            />
                        </div>
                        <ImagePickerField
                            label="Image URL"
                            value={form.imageUrl}
                            onChange={(next) => setForm({ ...form, imageUrl: next })}
                            token={token}
                            required
                            helperText="Pick a curated image for the collection card."
                            preferredVariantType={2}
                        />
                        <div className={styles.field}>
                            <label>Tags</label>
                            <div className={styles.actions}>
                                <input
                                    value={tagInput}
                                    onChange={(event) => {
                                        const value = event.target.value;
                                        setTagInput(value);
                                        addExistingTagByName(value);
                                    }}
                                    list="collection-tag-list"
                                    placeholder="Search existing tags"
                                    maxLength={200}
                                />
                                <datalist id="collection-tag-list">
                                    {availableTags.map((tag) => (
                                        <option key={tag} value={tag} />
                                    ))}
                                </datalist>
                                <button type="button" className={styles.secondary} onClick={createTag}>
                                    Create new
                                </button>
                            </div>
                            <div className={styles.actions}>
                                {form.tags.map((tag) => (
                                    <button key={tag} type="button" className={styles.secondary} onClick={() => removeTag(tag)}>
                                        #{tag} x
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div className={styles.field}>
                            <label htmlFor="collection-start">Start Date</label>
                            <AdminDateTimeInput
                                id="collection-start"
                                value={form.startDate ?? ''}
                                includeTime
                                onChange={(value) => setForm({ ...form, startDate: value })}
                            />
                        </div>
                        <div className={styles.field}>
                            <label htmlFor="collection-end">End Date</label>
                            <AdminDateTimeInput
                                id="collection-end"
                                value={form.endDate ?? ''}
                                includeTime
                                onChange={(value) => setForm({ ...form, endDate: value })}
                            />
                        </div>
                        <div className={styles.field}>
                            <label htmlFor="collection-sort">Sort Order</label>
                            <input
                                id="collection-sort"
                                type="number"
                                value={form.sortOrder}
                                onChange={(event) => setForm({ ...form, sortOrder: Number(event.target.value) })}
                            />
                        </div>
                        <div className={styles.field}>
                            <label>
                                <input
                                    type="checkbox"
                                    checked={form.isActive}
                                    onChange={(event) => setForm({ ...form, isActive: event.target.checked })}
                                />
                                {' '}Active
                            </label>
                        </div>
                        <div className={styles.actions}>
                            <button className={styles.primary} type="submit">
                                {selectedId ? 'Update Collection' : 'Create Collection'}
                            </button>
                            {selectedId ? (
                                <button type="button" className={styles.secondary} onClick={resetForm}>Cancel</button>
                            ) : null}
                        </div>
                    </form>
                </section>
            </div>
        </div>
    );
};

export default AdminCollections;
